import { AppDispatch } from '../../../store'
import { getNotifications, markNotificationAsRead, markAllNotificationsAsRead, deleteNotification, deleteAllNotifications } from '../../api'
import { setNotifications, markAsRead, markAllAsRead, removeNotification, clearNotifications } from './reducer'

export const fetchNotifications = (walletAddress: string, type?: string) => async (dispatch: AppDispatch, getState: () => any) => {
  try {
    const data = await getNotifications(walletAddress, type)
    if (data && data.notifications) {
      // Normalize notifications from backend
      const normalizedNotifications = data.notifications.map((n: any) => ({
        ...n,
        id: n._id || n.id,
        read: n.isRead ?? n.read ?? false,
        type: n.type || 'system',
        timestamp: n.createdAt ? new Date(n.createdAt).getTime() : Date.now(),
      }))
      dispatch(setNotifications(normalizedNotifications))
    }
    return { 
      success: true, 
      unreadCount: data?.unreadCount || 0,
      unreadCounts: data?.unreadCounts || {
        messages: 0,
        war: 0,
        alliance: 0,
        system: 0,
        reports: 0,
        diplomacy: 0,
      }
    }
  } catch (error: any) {
    console.error('Error fetching notifications:', error)
    return { success: false, message: error?.message || 'Failed to fetch notifications' }
  }
}

/**
 * True only for an id that came from the database — a 24-character hex ObjectId.
 *
 * Several notifications are raised locally for events in flight (marches, rallies, the
 * "your host rides for home" homecoming) under synthetic ids like `mvm-return-1234`. They have no
 * document behind them, so read/delete only has to touch Redux; sending one to the server is a
 * round trip that can only fail.
 */
const isStoredNotificationId = (id: string): boolean => /^[0-9a-f]{24}$/i.test(String(id ?? ''))

export const markNotificationRead = (walletAddress: string, notificationId: string) => async (dispatch: AppDispatch) => {
  try {
    if (!isStoredNotificationId(notificationId)) {
      dispatch(markAsRead(notificationId))
      return { success: true }
    }
    await markNotificationAsRead(walletAddress, notificationId)
    dispatch(markAsRead(notificationId))
    return { success: true }
  } catch (error: any) {
    console.error('Error marking notification as read:', error)
    return { success: false, message: error?.message || 'Failed to mark notification as read' }
  }
}

export const markAllNotificationsRead = (walletAddress: string) => async (dispatch: AppDispatch) => {
  try {
    await markAllNotificationsAsRead(walletAddress)
    dispatch(markAllAsRead())
    return { success: true }
  } catch (error: any) {
    console.error('Error marking all notifications as read:', error)
    return { success: false, message: error?.message || 'Failed to mark all notifications as read' }
  }
}

export const deleteNotificationAction = (walletAddress: string, notificationId: string) => async (dispatch: AppDispatch) => {
  try {
    // Locally-raised notification: nothing to soft-delete server-side, just drop it from the list.
    if (!isStoredNotificationId(notificationId)) {
      dispatch(removeNotification(notificationId))
      return { success: true }
    }
    await deleteNotification(walletAddress, notificationId)
    dispatch(removeNotification(notificationId))
    return { success: true }
  } catch (error: any) {
    console.error('Error deleting notification:', error)
    return { success: false, message: error?.message || 'Failed to delete notification' }
  }
}

export const deleteAllNotificationsAction = (walletAddress: string) => async (dispatch: AppDispatch) => {
  try {
    await deleteAllNotifications(walletAddress)
    dispatch(clearNotifications())
    return { success: true }
  } catch (error: any) {
    console.error('Error deleting all notifications:', error)
    return { success: false, message: error?.message || 'Failed to delete all notifications' }
  }
}

